import { motion, useDragControls, useIsPresent, useReducedMotion } from 'framer-motion';
import { createContext, type ReactNode, useContext, useLayoutEffect, useRef } from 'react';
import { exitIn, INSTANT, MOVE, SPRING_CARD, SPRING_PANEL } from '../../lib/motion.js';
import { setCover } from '../../lib/stage/cover.js';
import { type StageOrigin, useStageOrigin } from './origin.js';

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6 6 18"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path
      d="M14.5 5.5 8 12l6.5 6.5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/** A origem em palavras: o pedido entendido, ou quem trouxe o painel de volta. */
function originLine(origin: StageOrigin | null): string | null {
  if (!origin) return null;
  if (origin.source === 'history') return 'De volta ao que você estava vendo';
  if (origin.source !== 'assistant') return null;
  const asked = origin.asked?.trim();
  if (!asked) return 'Abri para você';
  const short = asked.length > 64 ? `${asked.slice(0, 62).trimEnd()}…` : asked;
  return `Você pediu: “${short.replace(/[.!?…]+$/, '')}”`;
}

/**
 * Dentro do Hoje fixo (lib/stage/dock.ts) o painel não sobe nem desce sozinho: é uma vista
 * da própria folha. A base é o Hoje; Agenda, Dinheiro e Documentos entram por cima, de lado,
 * com voltar. Quem arrasta e mede é a folha.
 */
export type DockView = { role: 'base' | 'top'; back: () => void };
export const DockViewContext = createContext<DockView | null>(null);

/**
 * Painel do palco: sobe do rodapé, ocupa a parte de baixo da tela e deixa o
 * personagem (em busto) à vista. Fecha deslizando para baixo pela alça ou pelo
 * cabeçalho, pelo X, tocando fora ou com o voltar do aparelho.
 */
export function PanelLayer({
  kicker,
  title,
  onClose,
  children,
  footer,
  tall = false,
}: {
  /** O que foi pedido, em poucas palavras (ex.: "Você abriu a agenda"). */
  kicker?: string;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Painel de formulário: pode ocupar quase a tela toda. */
  tall?: boolean;
}) {
  const reduced = useReducedMotion();
  const controls = useDragControls();
  const origin = useStageOrigin();
  const from = originLine(origin);
  const dock = useContext(DockViewContext);
  // A altura do painel fica à vista de quem desenha o resto: o personagem ocupa o
  // espaço que sobra acima dele, e um cartão que chegar junto (a previsão, uma
  // confirmação) sobe para cima do painel em vez de cobrir o título.
  const sectionRef = useRef<HTMLElement | null>(null);
  // Ao fechar, a cobertura é liberada no mesmo instante em que o painel começa a
  // descer (não quando ele desmonta): o personagem cresce junto, numa curva só.
  const isPresent = useIsPresent();
  useLayoutEffect(() => {
    const element = sectionRef.current;
    if (!element || !isPresent) return;
    const publish = () => setCover('panel', element.offsetHeight);
    publish();
    const watcher = new ResizeObserver(publish);
    watcher.observe(element);
    return () => {
      watcher.disconnect();
      setCover('panel', null);
    };
  }, [isPresent]);
  if (dock) {
    return (
      <section className={`stg-dockview ${dock.role}`} role="region" aria-label={title}>
        <header className="stg-panel-hd hj-dock-view-hd">
          {dock.role === 'top' && (
            <button className="stg-back" onClick={dock.back} aria-label="Voltar ao Hoje">
              <BackIcon />
            </button>
          )}
          <div className="stg-panel-title">
            {kicker && <small>{kicker}</small>}
            <h2>{title}</h2>
            {from && <p className="stg-origin">{from}</p>}
          </div>
        </header>
        <div className="stg-panel-body">{children}</div>
        {footer && <footer className="stg-panel-ft">{footer}</footer>}
      </section>
    );
  }
  return (
    <motion.div
      className="stg-panelwrap"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={reduced ? INSTANT : { duration: MOVE.panel * 0.6 }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.section
        className={`stg-panel${tall ? ' tall' : ''}`}
        ref={sectionRef}
        role="dialog"
        aria-label={title}
        initial={{ y: '104%' }}
        animate={{ y: 0 }}
        exit={{ y: '104%', transition: reduced ? INSTANT : exitIn(MOVE.panel) }}
        transition={reduced ? INSTANT : SPRING_PANEL}
        drag="y"
        dragControls={controls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.7 }}
        onDragEnd={(_, info) => {
          if (info.offset.y > 110 || info.velocity.y > 620) onClose();
        }}
      >
        <header
          className="stg-panel-hd"
          onPointerDown={(event) => {
            if ((event.target as Element).closest('button, a')) return;
            controls.start(event);
          }}
        >
          <span className="stg-grip" aria-hidden="true" />
          {origin?.back && (
            <button
              className="stg-back"
              onClick={origin.back}
              aria-label="Voltar ao painel anterior"
            >
              <BackIcon />
            </button>
          )}
          <div className="stg-panel-title">
            {kicker && <small>{kicker}</small>}
            <h2>{title}</h2>
            {from && <p className="stg-origin">{from}</p>}
          </div>
          <button className="stg-x" onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </button>
        </header>
        <div className="stg-panel-body">{children}</div>
        {footer && <footer className="stg-panel-ft">{footer}</footer>}
      </motion.section>
    </motion.div>
  );
}

/**
 * Cartão: uma informação ou confirmação curta, acima do botão Falar. Fecha no "x",
 * deslizando para baixo, com o voltar do aparelho ou sozinho depois de um tempo.
 */
export function CardLayer({
  children,
  onClose,
  slim = false,
}: {
  children: ReactNode;
  onClose: () => void;
  /** Sem espaço para o cartão inteiro e o rosto: uma linha só. */
  slim?: boolean;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={`stg-card${slim ? ' slim' : ''}`}
      role="status"
      layout={!reduced}
      initial={{ opacity: 0, y: 18, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, transition: reduced ? INSTANT : exitIn(MOVE.card) }}
      transition={reduced ? INSTANT : SPRING_CARD}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.8 }}
      onDragEnd={(_, info) => {
        if (info.offset.y > 40 || info.velocity.y > 400) onClose();
      }}
    >
      {children}
      <button
        type="button"
        className="stg-card-x"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onClose}
        aria-label="Fechar"
      >
        <CloseIcon />
      </button>
    </motion.div>
  );
}
